import { z } from 'zod';

/**
 * The encrypted envelope is the only shape in which conversation content ever
 * crosses the network or reaches storage.
 *
 * The server treats an envelope as opaque: it validates the structure so that
 * malformed rows cannot be stored, and never attempts to interpret `ct`.
 *
 * Documented in docs/security/encryption-and-key-lifecycle.md.
 */

/** Envelope format version. Bump when the wire shape changes incompatibly. */
export const ENVELOPE_VERSION = 1 as const;

/** The only content-encryption algorithm used in V1. */
export const ENVELOPE_ALG = 'A256GCM' as const;

/** AES-GCM nonce length in bytes. Unique per encryption, never reused. */
export const NONCE_BYTES = 12;

/** Conversation key length in bytes (AES-256). */
export const CONVERSATION_KEY_BYTES = 32;

/** base64url, unpadded. */
const base64url = z
  .string()
  .min(1)
  .max(4_000_000)
  .regex(/^[A-Za-z0-9_-]+$/, 'expected unpadded base64url');

export const encryptedEnvelopeSchema = z.object({
  /** Envelope format version. */
  v: z.literal(ENVELOPE_VERSION),
  /** Content-encryption algorithm. AES-256-GCM only in V1. */
  alg: z.literal(ENVELOPE_ALG),
  /** Per-message random nonce, 12 bytes, base64url. Never reused with one key. */
  iv: base64url,
  /** Ciphertext including the GCM authentication tag, base64url. */
  ct: base64url,
});

export type EncryptedEnvelope = z.infer<typeof encryptedEnvelopeSchema>;

/**
 * Additional authenticated data.
 *
 * AAD binds a ciphertext to its conversation, its sender role and its content
 * kind, so a ciphertext cannot be replayed into a different conversation or
 * relabelled as a different kind of content. It is authenticated, not
 * encrypted, and contains only values the server already knows.
 *
 * Server-assigned values (sequence number, timestamps) are deliberately absent:
 * the client cannot know them at encryption time.
 */
export interface EnvelopeAad {
  v: typeof ENVELOPE_VERSION;
  /** Opaque conversation identifier. */
  cid: string;
  /** Which participant produced the content. */
  sender: ParticipantRole;
  /** What the plaintext is. */
  kind: ContentKind;
}

/** Canonical, stable serialisation of the AAD. Both sides must agree byte for byte. */
export function serialiseAad(aad: EnvelopeAad): string {
  return JSON.stringify([aad.v, aad.cid, aad.sender, aad.kind]);
}

/**
 * AAD conversation identifier used for the visitor display name.
 *
 * The display name is encrypted in the visitor's browser **before** the Join
 * request, which is the moment the opaque conversation identifier is created.
 * There is therefore no conversation id to bind to yet, so this fixed constant
 * is used instead and both sides agree on it. Every other payload binds to the
 * real conversation identifier.
 */
export const VISITOR_NAME_AAD_CID = 'invitation';

export const participantRoles = ['owner', 'visitor'] as const;
export type ParticipantRole = (typeof participantRoles)[number];

/**
 * Kinds of conversation content. Every one of these is encrypted client-side
 * before it reaches the server (concept §6).
 *
 * `visitor_name` is included deliberately: the artifact carries the visitor
 * display name as conversation content, not as metadata.
 */
export const contentKinds = ['text', 'photo', 'video', 'file', 'voice', 'visitor_name'] as const;
export type ContentKind = (typeof contentKinds)[number];

/** Message kinds that may appear on the wire as a chat message. */
export const messageKinds = ['text', 'photo', 'video', 'file', 'voice'] as const;
export type MessageKind = (typeof messageKinds)[number];

export const messageKindSchema = z.enum(messageKinds);

/**
 * Plaintext shape of a `text` message, before encryption.
 * Only ever exists inside a browser.
 */
export const textPayloadSchema = z.object({
  kind: z.literal('text'),
  /** The written message. */
  body: z.string().min(1).max(8000),
  /** BCP-47 tag the sender believes they wrote in, used for on-device translation. */
  lang: z.string().max(35).optional(),
});
export type TextPayload = z.infer<typeof textPayloadSchema>;

/**
 * Plaintext shape of a media/file/voice message, before encryption.
 *
 * The filename lives here, inside the ciphertext. The server only ever sees the
 * opaque object key it generated itself, and a byte size.
 */
export const attachmentPayloadSchema = z.object({
  kind: z.enum(['photo', 'video', 'file', 'voice']),
  /** Original filename. Never leaves the ciphertext. */
  filename: z.string().min(1).max(512),
  /** Original media type. Never leaves the ciphertext. */
  mimeType: z.string().min(1).max(255),
  /** Plaintext byte length, before encryption. */
  size: z
    .number()
    .int()
    .nonnegative()
    .max(1024 * 1024 * 1024),
  /** Optional caption typed by the sender. */
  caption: z.string().max(2000).optional(),
  /**
   * Nonce used to encrypt the object body in storage, base64url.
   *
   * It lives inside the encrypted message payload rather than beside the object
   * so that storage metadata reveals nothing at all about the attachment. The
   * server stores it without ever knowing what it is.
   */
  objectIv: z
    .string()
    .regex(/^[A-Za-z0-9_-]+$/)
    .max(64)
    .optional(),
  /** Duration in seconds, for voice and video. */
  durationSeconds: z.number().nonnegative().max(86400).optional(),
  /** Pixel dimensions, for photo and video. */
  width: z.number().int().positive().max(100000).optional(),
  height: z.number().int().positive().max(100000).optional(),
});
export type AttachmentPayload = z.infer<typeof attachmentPayloadSchema>;

/** Plaintext shape of the visitor display name, before encryption. */
export const visitorNamePayloadSchema = z.object({
  kind: z.literal('visitor_name'),
  displayName: z.string().min(1).max(80),
});
export type VisitorNamePayload = z.infer<typeof visitorNamePayloadSchema>;

export const conversationPayloadSchema = z.discriminatedUnion('kind', [
  textPayloadSchema,
  attachmentPayloadSchema.extend({ kind: z.literal('photo') }),
  attachmentPayloadSchema.extend({ kind: z.literal('video') }),
  attachmentPayloadSchema.extend({ kind: z.literal('file') }),
  attachmentPayloadSchema.extend({ kind: z.literal('voice') }),
  visitorNamePayloadSchema,
]);
export type ConversationPayload = z.infer<typeof conversationPayloadSchema>;
